/**
 * InteriorLiftModule — THE LIFT (the interiors programme, slice L, 2026-10-01).
 *
 * A shell of three floors or more is cut with a lift shaft (`building_interiors.interiorSpec` — the planner's own
 * candidate cell). This module is the car that rides in it, for the building the player is standing in:
 *
 *   · it follows `atlas.interiorId` and reads the room through `activeInterior()` — it never touches the visit
 *     (`BuildingInteriorModule`). Its group, its doors and its car are its own scene objects;
 *   · it opens and closes the visit's shaft PLUGS by their collider ids (`int:<id>:<n>` — `plugsOpen`): the shaft is
 *     solid except where the car stands open, and the floor a rider stands on is 3.4 m of plug;
 *   · the pure machine (`lib/interior_lift.ts`) runs on the FIXED tick, one step before the avatar's, so the car and
 *     the capsule move in lockstep: `PhysicsModule.setRide` holds the feet on the car's floor while it travels;
 *   · E (the interact action — keyboard, pad, touch, XR) at a call plate or inside the car goes through the ONE KEY,
 *     ONE FOCUS arbiter and opens a small floor picker; E again ends any wait at once;
 *   · THE SHAFT VETO: where a building's plan stands furniture in the shaft's own cell (a legacy-fallback floor, a
 *     classic pack room) the shell is cut WITHOUT a shaft — this module answers the shell's question at the door, from
 *     the plan it warmed while the player walked up.
 *
 * The car is client-local presentation: every player rides their own. A ride moves nothing in XZ and walking never
 * writes Y on the server, so the row stays where the rider stands. Levers: `localStorage atlas:interior-lift = 'off'`
 * (no shaft, no car — the shell exactly as before slice L); the planner's lever parks it too.
 */
import * as THREE from 'three/webgpu';
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import {
  enterables, interiorSpec, setLiftShaftVeto, liftShaftEnabled, FLOOR_H, LIFT_DOOR_H,
  type EnterableBuilding, type InteriorSpec, type LiftShaft, type IntBox,
} from '../lib/building_interiors.js';
import {
  LIFT_HZ, floorY, floorAtHeight, floorName, senseBody, liftService, shaftConflict, liftCall, liftSelect,
  liftSkip, liftRunInit, liftRunStep, liftRunAbort, liftRunStop, cabParts, leafPart, landingParts, callPlate, staticFixtureBoxes, cabRideRect, shaftToLocal, pickerRows, cycleDest,
  type LiftState, type LiftRun, type LiftHost, type LiftService, type LiftPart, type LiftRole, type LiftEvent, type WalkSolid,
} from '../lib/interior_lift.js';
import {
  activeInterior, planInputFor, planCachePeek, planCached, layoutFor, isPackInterior, furnishRouteFor, seatView, residentRoomOf,
  type LayoutPin, type DetailLevel,
} from '../lib/interior_wiring.js';
import type { PlanInput } from '../lib/interior_planner.js';
import { liftShare } from '../lib/interior_life.js';
import { partitionSolids } from '../lib/interior_life_visit.js';
import { planRoom } from './interior/interior_plan_client.js';
import { resolvePurpose } from '../lib/interior_purpose.js';
import { shellMaterials, completeLook, type InteriorLook, type ShellMaterials } from './interior/interior_look.js';
import { clampDetail, styleFor } from '../lib/interior_style.js';
import { furnishPack, type FurnishPack } from '../lib/furnish_packs.js';
import { activePreset } from '../lib/quality_tiers.js';
import { interactFocus } from '../lib/interact_focus.js';
import { sfxAt, sfxLoop, stopSfxScope, type LoopHandle } from '../lib/sfx.js';
import { keyHint } from '../lib/keybinds.js';
import { comfort } from '../lib/xr_comfort.js';
import { deferDisposeTree } from '../lib/defer_dispose.js';
import { disposeLater } from '../lib/gpu_dispose.js';
import { mergeStaticMeshes } from '../lib/static_merge.js';
import type { PhysicsModule } from './PhysicsModule.js';

/** The storey whose floor is nearest a height. */
const nearestFloor = (y: number, floors: number): number => { let best = 0, bd = Infinity; for (let f = 0; f < floors; f++) { const d = Math.abs(floorY(f) - y); if (d < bd) { bd = d; best = f; } } return best; };

/** A press at the call plate counts within this of the call post (m), on the post's storey. */
const USE_M = 1.9;
/** The plan of a door this near is warmed (m) — so the shell's question at the door is already answered. */
const WARM_M = 18;
const SOUND_SCOPE = 'interior-lift';

interface Rig {
  id: string;
  /** The building's pose at entry (it does not move; the registry entry may be replaced by a rebuild). */
  x: number; z: number; yaw: number; groundY: number; key: string;
  spec: InteriorSpec; shaft: LiftShaft; collide: IntBox[];
  group: THREE.Group; cab: THREE.Group;
  leaves: Array<[THREE.Mesh, THREE.Mesh]>;
  callLights: THREE.Mesh[];
  mats: Record<LiftRole, THREE.Material>; litMat: THREE.Material; spare: THREE.Material[];
  /** The car: the machine, the platform's hold and the plugs (`lib/interior_lift.ts` — the gate runs the same step). */
  run: LiftRun; state: LiftState; host: LiftHost;
  service: LiftService | null; serviceVersion: number;
  leafFloor: number; leafOpen: number;
  fixturesUuid: string; fixtureUndo: Array<() => void>; fixturesTaken: number;
  hum: LoopHandle | null;
}

export class InteriorLiftModule implements IModule {
  readonly name = 'InteriorLift';
  private scene!: THREE.Scene;
  private rig: Rig | null = null;
  /** Why the active interior has no car ('' while one runs, or while outdoors). */
  private idle: { id: string; why: string } | null = null;
  private prompt: HTMLDivElement | null = null;
  private picker: { el: HTMLDivElement; mode: 'landing' | 'cab'; floor: number; rows: number[]; index: number } | null = null;
  private frameN = 0;
  private readonly stats = { rides: 0, skips: 0, aborts: 0, clips: 0, syncPlans: 0, warmed: 0, vetoes: 0, lostRider: 0, visits: 0 };
  // THE SHAFT VETO
  private near: EnterableBuilding | null = null;
  private inVeto = false;
  private readonly verdicts = new Map<string, { sig: string; veto: boolean }>();
  private readonly warming = new Set<string>();
  private onKey = (e: KeyboardEvent): void => this.pickerKey(e);

  constructor(private readonly physics: PhysicsModule) {}

  init(app: App): void {
    this.scene = app.world.scene;
    const p = document.createElement('div');
    p.id = 'interior-lift-prompt';
    p.style.cssText = 'position:fixed;left:50%;bottom:296px;transform:translateX(-50%);z-index:41;display:none;'
      + 'padding:6px 14px;border-radius:var(--ax-radius);background:color-mix(in srgb, var(--ax-bg) 86%, transparent);border:1px solid color-mix(in srgb, var(--ax-accent) 55%, transparent);'
      + 'font:600 13px var(--ax-font);color:var(--ax-text);pointer-events:none';
    document.body.appendChild(p);
    this.prompt = p;
    setLiftShaftVeto((key, w, h, d) => this.veto(key, w, h, d));
    app.world.addSystem(makeSystem('InteriorLiftFixed', Phase.FixedSim, 39, () => this.fixed())); // one step before the avatar's (40)
    app.world.addSystem(makeSystem('InteriorLift', Phase.UI, 35, () => this.frame()));           // after the visit's (34: it flips the arbiter)
    bus.on('atlas:place-requested', () => this.interact());
    bus.on('atlas:world-cleared', () => { this.teardown(false); this.verdicts.clear(); this.near = null; });
    window.addEventListener('keydown', this.onKey, true);
    (window as unknown as { __lift?: unknown }).__lift = () => this.diag();
  }

  dispose(): void {
    this.teardown(true);
    setLiftShaftVeto(null);
    window.removeEventListener('keydown', this.onKey, true);
    this.prompt?.remove(); this.prompt = null;
    const w = window as unknown as { __lift?: unknown };
    delete w.__lift;
  }

  // ─── THE SHAFT VETO (asked by `interiorSpec` while the visit cuts the shell) ──────────────────────────────────────────

  private pinOf(b: EnterableBuilding): LayoutPin | null {
    if (!b.id.startsWith('deed:')) return null;
    const db = atlas.conn?.db as unknown as { structureLayout?: { structureId: { find(id: bigint): { layoutRevision: number; digest: string } | undefined } } } | undefined;
    try { const row = db?.structureLayout?.structureId.find(BigInt(b.id.slice(5))); return row ? { layoutRevision: row.layoutRevision, digest: row.digest } : null; } catch { return null; }
  }
  private packOf(b: EnterableBuilding): FurnishPack | null {
    if (!b.id.startsWith('deed:')) return null;
    const db = atlas.conn?.db as unknown as { structureFurnish?: { structureId: { find(id: bigint): { pack: number } | undefined } } } | undefined;
    try { const row = db?.structureFurnish?.structureId.find(BigInt(b.id.slice(5))); return row ? furnishPack(row.pack) : null; } catch { return null; }
  }
  /** The plan the visit will make for this building (the SAME input: purpose, pin, tradition), and what tells one
   *  verdict from the next (a pack bought, a pin moved, the detail level). */
  private planOf(b: EnterableBuilding): { input: PlanInput; pack: FurnishPack | null | undefined; detail: DetailLevel; sig: string } {
    const purpose = resolvePurpose(b.id, b.key).purpose;
    const packed = isPackInterior(b.id, b.key);
    const pin = packed ? this.pinOf(b) : null, pack = packed ? this.packOf(b) : undefined;
    const layout = layoutFor(b.id, b.key, pin);
    // the look WITHOUT its room volume: completing it resolves who built it and never touches the room audio
    const look = completeLook({ id: b.id, key: b.key, purpose, x: b.x, z: b.z, yaw: b.yaw, groundY: b.groundY });
    const tradition = styleFor(look).tradition;
    const detail = clampDetail(activePreset().interiorDetail) as DetailLevel;
    return { input: planInputFor(b, purpose, layout, tradition), pack, detail, sig: `${layout}|${pack ? pack.pack : '-'}|${detail}|${tradition}` };
  }
  /** Does this building's plan stand furniture in the shaft's cell? `sync` plans on a cache miss (the door press);
   *  otherwise null = not known yet. */
  private verdict(b: EnterableBuilding, sync: boolean): boolean | null {
    const spec = interiorSpec(b.w, b.h, b.d, b.key, { lift: true });
    if (!spec.lift) return false;
    const plan = this.planOf(b);
    const hit = this.verdicts.get(b.id);
    if (hit && hit.sig === plan.sig) return hit.veto;
    let res = planCachePeek(plan.input);
    if (!res) {
      if (!sync) return null;
      res = planCached(plan.input); // the visit's own cache: its `planRoom` is then a hit
      this.stats.syncPlans++;
    }
    const d = res.descriptor;
    let veto = false;
    if (furnishRouteFor(b.id, b.key, true, d) === 'descriptor') {
      const view = seatView(d, { pack: plan.pack, detail: plan.detail });
      veto = shaftConflict(spec.lift, residentRoomOf(d, view).solids);
    }
    this.verdicts.set(b.id, { sig: plan.sig, veto });
    if (this.verdicts.size > 256) this.verdicts.delete(this.verdicts.keys().next().value as string);
    return veto;
  }
  private veto(key: string | undefined, w: number, h: number, d: number): boolean {
    if (this.inVeto || !key) return false;
    const b = this.near;
    if (!b || b.key !== key || b.w !== w || b.h !== h || b.d !== d) return false;
    this.inVeto = true; // planning cuts the shell again: that inner ask is not about a door
    try { const v = this.verdict(b, true) === true; if (v) this.stats.vetoes++; return v; } catch (e) { console.warn('[InteriorLift] the shaft verdict failed — the shell keeps its shaft', e); return false; } finally { this.inVeto = false; }
  }
  /** ~4 Hz: which lift-capable door the player is at, and its plan warmed off this frame. */
  private scanDoors(): void {
    if (!liftShaftEnabled()) { this.near = null; return; }
    const inside = atlas.interiorId ? enterables.get(atlas.interiorId) ?? null : null;
    let best: EnterableBuilding | null = inside, bd = Infinity;
    if (!inside) {
      const px = atlas.localX, pz = atlas.localZ;
      for (const b of enterables.values()) {
        if (Math.abs(b.x - px) > 48 || Math.abs(b.z - pz) > 48) continue;
        if (Math.floor((b.h - 1.6) / FLOOR_H) < 3) continue;
        const hd = Math.min(b.d, 60) / 2 - 0.45;
        const dx = b.x - hd * Math.sin(b.yaw) - px, dz = b.z - hd * Math.cos(b.yaw) - pz; // the door: local (0, −hd)
        const dist = Math.hypot(dx, dz);
        if (dist < WARM_M && dist < bd) { bd = dist; best = b; }
      }
    }
    this.near = best;
    if (!best || this.warming.has(best.id)) return;
    this.inVeto = true;
    try {
      if (this.verdict(best, false) !== null) return;
      const b = best, plan = this.planOf(b), spec = interiorSpec(b.w, b.h, b.d, b.key, { lift: true });
      this.warming.add(b.id);
      void planRoom(plan.input, spec).then(() => { this.stats.warmed++; }).catch(() => { /* the door press plans it */ }).finally(() => { this.warming.delete(b.id); });
    } catch { /* a plan that cannot be made is the visit's to report */ } finally { this.inVeto = false; }
  }

  // ─── The rig (one per visit) ──────────────────────────────────────────────────────────────────────────────────────

  private setup(id: string): void {
    const b = enterables.get(id);
    if (!b) return; // mid-rebuild: asked again next tick
    const none = (why: string): void => { this.idle = { id, why }; };
    if (!liftShaftEnabled()) return none('the lever is off');
    const spec = interiorSpec(b.w, b.h, b.d, b.key, { lift: true });
    if (!spec.lift) return none(spec.floors < 3 ? 'under three floors' : 'no legal place for a shaft');
    const collide = spec.boxes.filter((q) => q.collide);
    // THE GROUND TRUTH: the shell the visit built has the shaft iff its LAST collider index exists (a shaft only adds)
    if (!this.physics.hasStaticCollider(`int:${id}:${collide.length - 1}`)) return none('furniture stands in the shaft (the shell was cut without one)');
    const shaft = spec.lift;
    const purpose = resolvePurpose(b.id, b.key).purpose;
    const look: InteriorLook = { id: b.id, key: b.key, purpose, x: b.x, z: b.z, yaw: b.yaw, groundY: b.groundY, floors: spec.floors, hw: spec.hw, hd: spec.hd };
    let shell: ShellMaterials | null = null;
    try { shell = shellMaterials(look); } catch { shell = null; }
    const lm = shell?.lift;
    const basic = (hex: number, name: string): THREE.Material => Object.assign(new THREE.MeshBasicMaterial({ color: hex }), { name });
    const mats: Record<LiftRole, THREE.Material> = lm
      ? { frame: lm.frame, cab: lm.cab, door: lm.door, call: lm.call, ring: lm.ring }
      : { frame: basic(0x3a4048, 'interior-lift-frame'), cab: basic(0xd8cfbc, 'interior-lift-cab'), door: basic(0x9aa3ad, 'interior-lift-door'), call: basic(0xffb347, 'interior-lift-call'), ring: basic(0x6db3f2, 'interior-lift-ring') };
    const litMat = basic(0xfff1c8, 'interior-lift-lit'); // a call plate that has been pressed: brighter than the amber
    // the five shell materials that came with the lift's are not drawn by anything here: freed with the rig
    const spare: THREE.Material[] = shell ? [shell.wall, shell.floor, shell.stair, shell.trim, shell.glass] : [];

    const group = new THREE.Group();
    group.name = 'interior-lift';
    group.userData.interiorId = id;
    group.position.set(b.x, b.groundY, b.z); // a scene child is ABSOLUTE; everything under it is building-local
    group.rotation.y = b.yaw;
    const mesh = (p: LiftPart, y0: number, mat?: THREE.Material): THREE.Mesh => {
      const m = new THREE.Mesh(new THREE.BoxGeometry(p.w, p.h, p.d), mat ?? mats[p.role]);
      m.position.set(p.x, y0 + p.y, p.z);
      return m;
    };
    const fixed = new THREE.Group(); fixed.name = 'lift:landings';
    const leaves: Rig['leaves'] = [], callLights: THREE.Mesh[] = [];
    const plate = callPlate(shaft);
    for (let f = 0; f < spec.floors; f++) {
      const y0 = floorY(f);
      for (const p of landingParts(shaft)) fixed.add(mesh(p, y0));
      for (const p of plate.parts) {
        const m = mesh(p, y0);
        if (p.role === 'call') { m.name = 'lift:call-light'; callLights.push(m); group.add(m); } else fixed.add(m);
      }
      const leaf = (): THREE.Mesh => {
        const m = new THREE.Mesh(new THREE.BoxGeometry(1, 1, 1), mats.door);
        m.name = 'lift:leaf';
        group.add(m);
        return m;
      };
      leaves.push([leaf(), leaf()]);
    }
    mergeStaticMeshes(fixed);
    group.add(fixed);
    const cab = new THREE.Group(); cab.name = 'lift:cab';
    for (const p of cabParts(shaft)) cab.add(mesh(p, 0));
    mergeStaticMeshes(cab);
    group.add(cab);
    this.scene.add(group);

    const run = liftRunInit(spec.floors, 0);
    const rig: Rig = {
      id, x: b.x, z: b.z, yaw: b.yaw, groundY: b.groundY, key: b.key, spec, shaft, collide, group, cab, leaves, callLights, mats, litMat, spare,
      run, state: run.state, host: null as unknown as LiftHost, service: null, serviceVersion: -2, leafFloor: -1, leafOpen: -1,
      fixturesUuid: '', fixtureUndo: [], fixturesTaken: 0, hum: null,
    };
    rig.host = {
      plug: (f, solid) => this.plug(rig, f, solid),
      ride: (y) => this.physics.setRide(y === null ? null : this.rideOf(rig, y)),
      riding: () => this.physics.riding,
    };
    for (let f = 0; f < spec.floors; f++) this.poseLeaves(rig, f, 0);
    cab.position.y = rig.state.y;
    this.rig = rig;
    this.idle = null;
    this.stats.visits++;
    const share = liftShare();
    share.id = id; share.service = null; share.playerBusy = false;
  }

  /** The visit ended (its colliders are gone with it) or the module is going away while the visit lives on. */
  private teardown(visitLives: boolean): void {
    this.closePicker();
    this.showPrompt('');
    const r = this.rig;
    this.rig = null;
    this.idle = null;
    if (!r) return;
    const share = liftShare();
    if (share.id === r.id) { share.id = null; share.service = null; share.playerBusy = false; }
    stopSfxScope(SOUND_SCOPE);
    if (visitLives && atlas.interiorId === r.id) {
      // the shell stays: a body inside the car is stood on the nearest landing first, then every plug goes back
      const s = this.senseOf(r);
      if (r.run.riding || s.inCab || s.inDoorway) this.standAtCall(r, nearestFloor(r.state.y, r.spec.floors));
      liftRunStop(r.run, r.host);
      for (const undo of r.fixtureUndo) { try { undo(); } catch { /* the fixtures went with a re-plan */ } }
    } else {
      // THE VISIT ENDED (its colliders went with it): a rider is let go and stood on the building's ground — the
      // nearest floor there is — never dropped from the height the car had reached
      const was = r.run.riding || this.physics.riding;
      this.physics.setRide(null);
      if (was) this.physics.teleportAvatarY(atlas.localX, r.groundY + 0.14, atlas.localZ);
      r.run.plugOut.clear();
    }
    deferDisposeTree(r.group);
    disposeLater(r.litMat);
    for (const m of r.spare) disposeLater(m);
  }

  // ─── Geometry helpers ─────────────────────────────────────────────────────────────────────────────────────────────

  private toWorld(r: Rig, lx: number, lz: number): { x: number; z: number } {
    const c = Math.cos(r.yaw), s = Math.sin(r.yaw);
    return { x: r.x + lx * c + lz * s, z: r.z - lx * s + lz * c };
  }
  private toLocal(r: Rig, wx: number, wz: number): { x: number; z: number } {
    const c = Math.cos(r.yaw), s = Math.sin(r.yaw), dx = wx - r.x, dz = wz - r.z;
    return { x: c * dx - s * dz, z: s * dx + c * dz };
  }
  private senseOf(r: Rig): { inCab: boolean; inDoorway: boolean } {
    const l = this.toLocal(r, atlas.localX, atlas.localZ);
    return senseBody(r.shaft, l.x, l.z, atlas.localY - r.groundY, r.state.y);
  }
  private poseLeaves(r: Rig, f: number, open: number): void {
    for (const k of [0, 1] as const) {
      const p = leafPart(r.shaft, k, open), m = r.leaves[f][k];
      m.position.set(p.x, floorY(f) + p.y, p.z);
      m.scale.set(Math.max(0.001, p.w), p.h, Math.max(0.001, p.d));
    }
  }
  /** Add (`solid`) or remove one storey's plug — the visit's own collider, by its id. */
  private plug(r: Rig, f: number, solid: boolean): void {
    const n = r.shaft.plugs[f], cid = `int:${r.id}:${n}`;
    if (!solid) { this.physics.removeStaticCollider(cid); return; }
    const box = r.collide[n], wp = this.toWorld(r, box.x, box.z);
    this.physics.addStaticCollider(cid, { shape: 'box', halfExtents: [box.w / 2, box.h / 2, box.d / 2] }, { x: wp.x, y: r.groundY + box.y, z: wp.z, yaw: r.yaw, scale: 1 });
  }
  private rideOf(r: Rig, y: number): { interiorId: string; feetY: number; cx: number; cz: number; yaw: number; hx: number; hz: number } {
    const c = cabRideRect(r.shaft), w = this.toWorld(r, c.x, c.z);
    return { interiorId: r.id, feetY: r.groundY + y, cx: w.x, cz: w.z, yaw: r.yaw, hx: c.hx, hz: c.hz };
  }
  private cabWorld(r: Rig): { x: number; y: number; z: number } {
    const c = cabRideRect(r.shaft), w = this.toWorld(r, c.x, c.z);
    return { x: w.x, y: r.groundY + r.state.y + 1.2, z: w.z };
  }
  private standAtCall(r: Rig, f: number): void {
    const w = this.toWorld(r, r.shaft.call.x, r.shaft.call.z);
    this.physics.teleportAvatarY(w.x, r.groundY + floorY(f) + 0.02, w.z);
  }

  // ─── The fixed tick (FixedSim/39): the machine, the plugs, the platform ───────────────────────────────────────────

  private fixed(): void {
    const id = atlas.interiorId;
    if (this.rig && this.rig.id !== id) this.teardown(false); // the visit is over: its colliders went with it
    if (this.idle && this.idle.id !== id) this.idle = null;
    if (!id) return;
    if (!this.rig && !this.idle) this.setup(id);
    const r = this.rig;
    if (!r) return;
    if (!liftShaftEnabled()) { this.teardown(true); this.idle = { id, why: 'the lever is off' }; return; }
    this.refreshService(r);
    const st = r.state;
    st.instantRides = atlas.xrPresenting && comfort().move === 'teleport';
    // a lift that does not run stays shut: its machine steps only to finish what it was doing
    const stepping = !!r.service?.runs || st.phase !== 'idle' || st.door > 0;
    const events: LiftEvent[] = liftRunStep(r.run, this.senseOf(r), r.host, stepping);
    this.stats.lostRider = r.run.lost;
    for (const ev of events) this.onEvent(r, ev);
    // the residents' drawing hides anyone "in the car" while the player has it (there is ONE car on screen)
    liftShare().playerBusy = st.phase !== 'idle' || st.door > 0 || st.call >= 0 || st.dest >= 0;
    if (r.hum) {
      const moving = st.phase === 'travel' && st.mode !== 'instant';
      r.hum.at(this.cabWorld(r));
      r.hum.set(moving ? (st.rider ? 0.34 : 0.2) : 0, st.mode === 'express' ? 1.25 : 1, 0.25);
    }
  }

  private onEvent(r: Rig, ev: LiftEvent): void {
    const at = this.cabWorld(r);
    switch (ev) {
      case 'opening': case 'reopen': sfxAt('sfx_lift_door', at, { gain: 0.42, rate: 1.0, emitter: 'lift', cooldownMs: 250, caption: 'Lift doors' }); break;
      case 'closing': sfxAt('sfx_lift_door', at, { gain: 0.36, rate: 0.9, emitter: 'lift', cooldownMs: 250 }); break;
      case 'depart':
        if (r.state.rider) { this.stats.rides++; this.closePicker(); }
        if (!r.hum) r.hum = sfxLoop('sfx_lift_hum', SOUND_SCOPE);
        break;
      case 'arrive':
        sfxAt('sfx_lift_chime', at, { gain: 0.4, emitter: 'lift', cooldownMs: 400, caption: 'Lift' });
        break;
      default: break;
    }
  }

  /** Which storeys the lift serves follows the ROOM (the plan landed, a pack changed, a pin moved). */
  private refreshService(r: Rig): void {
    const act = activeInterior();
    if (!act || act.id !== r.id) return;
    if (act.version === r.serviceVersion) return;
    r.serviceVersion = act.version;
    if (act.route === 'pending') { r.service = null; liftShare().service = null; return; } // the plan is still being made: nothing is known to be clear
    const solids: WalkSolid[] = act.room?.solids ?? [];
    const svc = liftService(r.spec, solids, act.descriptor ? partitionSolids(act.descriptor) : []); // + the rooms' partitions (slice G)
    r.service = svc;
    liftShare().service = svc;
    if (svc.floors.some((f) => f.why === 'furniture stands in the shaft')) { this.stats.clips++; console.warn(`[InteriorLift] ${r.id}: drawn furniture stands in the shaft on ${svc.floors.filter((f) => f.why === 'furniture stands in the shaft').map((f) => floorName(f.floor)).join(', ')} — the lift does not stop there`); }
    const st = r.state;
    // the room changed under a car in use: it ends what it is doing at the nearest landing it still serves
    if (st.phase === 'travel' && (!svc.runs || !svc.served.includes(st.target))) {
      const f = liftRunAbort(r.run, svc.served, r.host);
      this.stats.aborts++;
      console.warn(`[InteriorLift] ${r.id}: the room changed mid-ride — the car stopped at ${floorName(f)}`);
    }
    if (st.dest >= 0 && !svc.served.includes(st.dest)) st.dest = -1;
    if (st.call >= 0 && !svc.served.includes(st.call)) st.call = -1;
  }

  // ─── The frame (UI/35): drawing, the visit's fixtures, the prompt, the picker ─────────────────────────────────────

  private frame(): void {
    this.frameN++;
    if (this.frameN % 15 === 0) this.scanDoors();
    const r = this.rig;
    if (!r) { this.showPrompt(''); if (this.picker) this.closePicker(); return; }
    const st = r.state;
    // ONE CAR ON SCREEN: the player's while they use it; otherwise the one the residents' rides imply (drawn only —
    // its landing stays solid to the player until they call the car themselves)
    const share = liftShare();
    const theirs = !share.playerBusy && share.id === r.id ? share.residentCab : null;
    const showY = theirs ? theirs.y : st.y, showFloor = theirs ? theirs.floor : st.floor, showDoor = theirs ? theirs.door : st.phase === 'travel' ? 0 : st.door;
    r.cab.position.y = showY;
    if (r.leafFloor !== showFloor || r.leafOpen !== showDoor) {
      if (r.leafFloor >= 0 && r.leafFloor !== showFloor) this.poseLeaves(r, r.leafFloor, 0);
      this.poseLeaves(r, showFloor, showDoor);
      r.leafFloor = showFloor; r.leafOpen = showDoor;
    }
    for (let f = 0; f < r.callLights.length; f++) {
      const lit = st.call === f || (st.phase === 'travel' && st.target === f);
      const want = lit ? r.litMat : r.mats.call;
      if (r.callLights[f].material !== want) r.callLights[f].material = want;
    }
    if (this.frameN % 15 === 7) this.takeOverFixtures(r);
    this.claimAndPrompt(r);
  }

  /** The visit draws slice F's static (closed) lift door and call panel from the plan. This module draws working ones:
   *  the static ones are found by where they stand and put away — a whole mesh is hidden, a merged one has those
   *  vertices gathered to a point. Undone if the module goes away while the visit lives on. */
  private takeOverFixtures(r: Rig): void {
    let root: THREE.Object3D | null = null;
    for (const o of this.scene.children) if (o.name === 'interior-fixtures') { root = o; break; }
    if (!root) { r.fixturesUuid = ''; r.fixtureUndo.length = 0; return; }
    if (root.uuid === r.fixturesUuid) return;
    r.fixturesUuid = root.uuid; r.fixtureUndo.length = 0; r.fixturesTaken = 0;
    const boxes = [...Array(r.spec.floors).keys()].flatMap((f) => staticFixtureBoxes(r.shaft, f));
    root.updateMatrixWorld(true);
    const inv = new THREE.Matrix4().copy(root.matrixWorld).invert(), rel = new THREE.Matrix4(), v = new THREE.Vector3();
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      const pos = m.isMesh ? (m.geometry?.attributes.position as THREE.BufferAttribute | undefined) : undefined;
      if (!pos || m.userData.sharedAsset || m.name === 'interior-plate') return;
      rel.multiplyMatrices(inv, m.matrixWorld);
      const hit: number[] = [];
      for (let i = 0; i < pos.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(rel);
        for (const q of boxes) if (v.x >= q.x0 && v.x <= q.x1 && v.y >= q.y0 && v.y <= q.y1 && v.z >= q.z0 && v.z <= q.z1) { hit.push(i); break; }
      }
      if (hit.length === 0) return;
      r.fixturesTaken++;
      if (hit.length === pos.count) { m.visible = false; r.fixtureUndo.push(() => { m.visible = true; }); return; }
      const keep = hit.map((i) => [pos.getX(i), pos.getY(i), pos.getZ(i)] as const);
      const ax = pos.getX(hit[0]), ay = pos.getY(hit[0]), az = pos.getZ(hit[0]);
      for (const i of hit) pos.setXYZ(i, ax, ay, az);
      pos.needsUpdate = true;
      r.fixtureUndo.push(() => { hit.forEach((i, k) => pos.setXYZ(i, keep[k][0], keep[k][1], keep[k][2])); pos.needsUpdate = true; });
    });
  }

  /** Where the player is against the lift: the storey, the distance to its call post, and whether they are in the car. */
  private where(r: Rig): { floor: number; dist: number; inCab: boolean; inDoorway: boolean } {
    const l = this.toLocal(r, atlas.localX, atlas.localZ);
    const ly = atlas.localY - r.groundY;
    const s = senseBody(r.shaft, l.x, l.z, ly, r.state.y);
    return { floor: floorAtHeight(ly, r.spec.floors), dist: Math.hypot(l.x - r.shaft.call.x, l.z - r.shaft.call.z), ...s };
  }

  private claimAndPrompt(r: Rig): void {
    const st = r.state, svc = r.service, w = this.where(r);
    const focus = interactFocus();
    const riding = st.phase === 'travel' && st.rider;
    const near = riding || w.inCab || w.dist <= USE_M;
    if (!near) { this.showPrompt(''); if (this.picker) this.closePicker(); return; }
    const key = keyHint('interact');
    let text = '', usable = true;
    if (!svc) { text = 'The lift is starting up…'; usable = false; }
    else if (!svc.runs) { text = `The lift is out of service — ${svc.why}`; usable = false; }
    else if (riding) text = `${floorName(st.target)} · ${key} — arrive now`;
    else if (w.inCab) text = st.dest >= 0 ? `${floorName(st.dest)} · ${key} — go now` : `${key} — choose a floor`;
    else if (!svc.served.includes(w.floor)) { text = `The lift does not stop on this floor — ${svc.floors[w.floor]?.why ?? ''}`; usable = false; }
    else if (st.call === w.floor || (st.phase === 'travel' && st.target === w.floor)) text = `The lift is coming · ${key} — hurry it`;
    else text = `${key} — lift`;
    if (usable || this.picker) focus.claim({ id: 'lift', dist: this.picker || riding || w.inCab ? 0 : w.dist, priority: 3, label: 'lift' });
    this.showPrompt(this.picker ? '' : (!usable || focus.owns('lift')) ? text : '');
    if (this.picker && this.picker.mode === 'landing' && (w.floor !== this.picker.floor || w.dist > USE_M + 0.6)) this.closePicker();
    if (this.picker && this.picker.mode === 'cab' && !w.inCab) this.closePicker();
  }

  private showPrompt(text: string): void {
    const p = this.prompt;
    if (!p) return;
    if (!text) { if (p.style.display !== 'none') p.style.display = 'none'; return; }
    if (p.textContent !== text) p.textContent = text;
    if (p.style.display === 'none') p.style.display = 'block';
  }

  // ─── The interact press ───────────────────────────────────────────────────────────────────────────────────────────

  private interact(): void {
    const r = this.rig;
    if (!r || atlas.loadingGate) return;
    if (this.picker) { this.confirm(this.picker.rows[this.picker.index]); return; }
    if (!interactFocus().owns('lift')) return; // ONE KEY, ONE FOCUS
    const st = r.state, svc = r.service;
    if (!svc?.runs) return;
    const w = this.where(r);
    if (st.phase === 'travel' && st.rider) { liftSkip(st); this.stats.skips++; return; }
    if (w.inCab) {
      if (st.dest >= 0) { liftSkip(st); this.stats.skips++; return; } // a storey is chosen: go now
      if (atlas.xrPresenting) { const d = cycleDest(svc.served, st.floor, st.dest); if (d >= 0) liftSelect(st, svc.served, d); return; }
      this.openPicker(r, 'cab', st.floor);
      return;
    }
    if (!svc.served.includes(w.floor)) return;
    if (st.call === w.floor || (st.phase === 'travel' && st.target === w.floor)) { liftSkip(st); this.stats.skips++; return; }
    if (atlas.xrPresenting) { liftCall(st, svc.served, w.floor, cycleDest(svc.served, w.floor, -1)); return; }
    this.openPicker(r, 'landing', w.floor);
  }

  private confirm(dest: number): void {
    const r = this.rig, pk = this.picker;
    if (!r || !pk || !r.service) { this.closePicker(); return; }
    if (dest === pk.floor) { this.closePicker(); return; }
    if (pk.mode === 'cab') liftSelect(r.state, r.service.served, dest);
    else liftCall(r.state, r.service.served, pk.floor, dest);
    sfxAt('sfx_button_press', this.cabWorld(r), { gain: 0.3, emitter: 'lift-button', cooldownMs: 120 });
    this.closePicker();
  }

  // ─── The floor picker (tokens only; keys, the pad's panel navigation, the pointer) ────────────────────────────────

  private openPicker(r: Rig, mode: 'landing' | 'cab', floor: number): void {
    this.closePicker();
    const svc = r.service;
    if (!svc) return;
    const rows = pickerRows(svc.served, floor);
    const el = document.createElement('div');
    el.id = 'interior-lift-picker';
    el.setAttribute('data-atlas-nav', 'lift-picker');
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-label', 'Lift — choose a floor');
    el.style.cssText = 'position:fixed;left:50%;bottom:250px;transform:translateX(-50%);z-index:44;min-width:230px;max-width:320px;padding:10px 12px 12px;'
      + 'border-radius:var(--ax-win-radius);background:var(--ax-win-bg);border:var(--ax-win-border);box-shadow:var(--ax-win-frame);'
      + 'font:13px var(--ax-font);color:var(--ax-text);display:flex;flex-direction:column;gap:6px';
    const head = document.createElement('div');
    head.style.cssText = 'display:flex;align-items:baseline;justify-content:space-between;gap:12px';
    const title = document.createElement('div');
    title.textContent = 'Lift';
    title.style.cssText = 'font:600 15px var(--ax-display);color:var(--ax-hi);letter-spacing:.04em';
    const close = document.createElement('button');
    close.type = 'button'; close.textContent = 'Close';
    close.setAttribute('data-atlas-close', '');
    close.className = 'ax-btn';
    close.addEventListener('click', () => this.closePicker());
    head.append(title, close);
    const sub = document.createElement('div');
    sub.textContent = mode === 'cab' ? `You are on ${floorName(floor).toLowerCase()}. Where to?` : `Calling from ${floorName(floor).toLowerCase()}. Where to?`;
    sub.style.cssText = 'color:var(--ax-soft)';
    const list = document.createElement('div');
    list.style.cssText = 'display:flex;flex-direction:column;gap:4px';
    const floors: number[] = [];
    for (const row of rows) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'ax-btn';
      b.setAttribute('data-floor', String(row.floor));
      b.style.cssText = 'display:flex;justify-content:space-between;gap:16px;text-align:left';
      const name = document.createElement('span'); name.textContent = row.name;
      const tag = document.createElement('span'); tag.className = 'ax-num'; tag.textContent = row.here ? 'here' : row.label;
      tag.style.color = 'var(--ax-muted)';
      b.append(name, tag);
      if (!row.enabled) { b.disabled = true; b.style.opacity = '0.55'; }
      else { floors.push(row.floor); b.addEventListener('click', () => this.confirm(row.floor)); }
      list.append(b);
    }
    const note = document.createElement('div');
    note.textContent = `The lifts are temperamental. Number keys, ↑ ↓ and ${keyHint('interact')} choose; Esc closes.`;
    note.style.cssText = 'color:var(--ax-muted);font-size:12px';
    el.append(head, sub, list, note);
    document.body.appendChild(el);
    // the first choice: the next storey up from here, else the top of the list
    const up = [...floors].filter((f) => f > floor).sort((a, b) => a - b)[0];
    this.picker = { el, mode, floor, rows: floors, index: Math.max(0, floors.indexOf(up ?? floors[0])) };
    this.markPicker();
  }
  private markPicker(): void {
    const pk = this.picker;
    if (!pk) return;
    for (const b of pk.el.querySelectorAll<HTMLButtonElement>('button[data-floor]')) {
      const on = Number(b.dataset.floor) === pk.rows[pk.index];
      if (on) b.setAttribute('data-ax-focus', ''); else b.removeAttribute('data-ax-focus');
      b.classList.toggle('is-primary', on);
    }
  }
  private closePicker(): void {
    if (!this.picker) return;
    this.picker.el.remove();
    this.picker = null;
  }
  /** While the picker is open its keys are the picker's alone (capture phase: nothing else sees them). */
  private pickerKey(e: KeyboardEvent): void {
    const pk = this.picker;
    if (!pk || e.ctrlKey || e.metaKey || e.altKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
    let took = true;
    const digit = /^(?:Digit|Numpad)(\d)$/.exec(e.code);
    if (digit) { const f = Number(digit[1]); if (pk.rows.includes(f) && !e.repeat) this.confirm(f); }
    else if (e.code === 'KeyG') { if (pk.rows.includes(0) && !e.repeat) this.confirm(0); }
    else if (e.code === 'ArrowUp') { pk.index = (pk.index + pk.rows.length - 1) % pk.rows.length; this.markPicker(); }
    else if (e.code === 'ArrowDown') { pk.index = (pk.index + 1) % pk.rows.length; this.markPicker(); }
    else if (e.code === 'Enter' || e.code === 'NumpadEnter') { if (!e.repeat) this.confirm(pk.rows[pk.index]); }
    else if (e.code === 'Escape') this.closePicker();
    else took = false;
    if (took) { e.preventDefault(); e.stopImmediatePropagation(); }
  }

  // ─── Diagnostics and probe levers ─────────────────────────────────────────────────────────────────────────────────

  private diag(): Record<string, unknown> {
    const r = this.rig;
    const base = {
      enabled: liftShaftEnabled(), inside: atlas.interiorId, idle: this.idle, stats: { ...this.stats }, near: this.near?.id ?? null,
      verdicts: this.verdicts.size, hz: LIFT_HZ, doorH: LIFT_DOOR_H,
    };
    if (!r) return { ...base, rig: null };
    const st = r.state, w = this.where(r);
    const call = this.toWorld(r, r.shaft.call.x, r.shaft.call.z);
    const cab = shaftToLocal(r.shaft, 0.8, 0.75), cabW = this.toWorld(r, cab.x, cab.z);
    return {
      ...base,
      rig: {
        id: r.id, floors: r.spec.floors, wall: r.shaft.wall, rect: r.shaft.rect, groundY: r.groundY,
        state: { phase: st.phase, floor: st.floor, target: st.target, y: Math.round(st.y * 1000) / 1000, door: Math.round(st.door * 100) / 100, call: st.call, dest: st.dest, rider: st.rider, mode: st.mode, t: st.t, ticks: st.ticks },
        service: r.service, plugsOut: [...r.run.plugOut].sort((a, b) => a - b), riding: r.run.riding, physicsRiding: this.physics.riding, settle: r.run.settle,
        where: { ...w, dist: Math.round(w.dist * 100) / 100 }, picker: this.picker ? { mode: this.picker.mode, floor: this.picker.floor, rows: this.picker.rows, index: this.picker.index } : null,
        fixturesTaken: r.fixturesTaken, callWorld: call, cabWorld: cabW, feetY: Math.round((atlas.localY - r.groundY) * 1000) / 1000,
        owns: interactFocus().owns('lift'), prompt: this.prompt && this.prompt.style.display !== 'none' ? this.prompt.textContent : '',
      },
      // probe levers (the real game presses the interact key)
      press: (): void => this.interact(),
      call: (at: number, dest = -1): boolean => !!r.service && liftCall(st, r.service.served, at, dest),
      select: (dest: number): boolean => !!r.service && liftSelect(st, r.service.served, dest),
      pick: (dest: number): void => this.confirm(dest),
      skip: (): void => liftSkip(st),
      /** Stand at a storey's call post or inside the car (the SERVER row moves too — a client-only hop is snapped back). */
      standAt: async (where: 'call' | 'cab', f: number): Promise<boolean> => {
        const p = where === 'cab' ? cabW : call;
        try { await (atlas.conn!.reducers as unknown as { debugTeleport(a: { posX: number; posZ: number }): Promise<void> }).debugTeleport({ posX: p.x, posZ: p.z }); } catch { /* no dev flag: the local hop alone */ }
        bus.emit('atlas:reconcile-hold', { ticks: 90, mode: 'settle' });
        this.physics.teleportAvatarY(p.x, r.groundY + floorY(f) + 0.02, p.z);
        return true;
      },
    };
  }
}
